import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Combobox,
  createTheme,
  Divider,
  Drawer,
  Flex,
  Group,
  Indicator,
  Input,
  type MantineThemeOverride,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  ModalRoot,
  MultiSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  PillsInput,
  Popover,
  Radio,
  ScrollArea,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";

import alertClasses from "./alert.module.css";
import comboboxClasses from "./comboboxOption.module.css";
import modalClasses from "./modal.module.css";
import navLinkClasses from "./navLink.module.css";
import sectionLabelClasses from "./sectionLabel.module.css";
import tooltipClasses from "./tooltip.module.css";

/**
 * DEFAULT_BLUE is the fallback ui palette used when no organization branding is
 * available (the org palette overrides this at runtime).
 */
const DEFAULT_BLUE: [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
] = [
  "#eff6ff",
  "#dbeafe",
  "#bfdbfe",
  "#93c5fd",
  "#60a5fa",
  "#3b82f6",
  "#2563eb",
  "#1d4ed8",
  "#1e40af",
  "#1e3a8a",
];

/**
 * INPUT_LABEL_MARGIN_BOTTOM is the label-to-input gap every labeled control carries
 * (see labelStyles below). It is exported because a hand-composed label row cannot
 * go through Mantine's own label slot and so inherits none of that treatment: the
 * activity chooser pairs its label with a guide link, which a Mantine `label` node
 * cannot hold without the link both opening the select and joining its accessible
 * name. Reading the gap from here keeps that row on the same rhythm as every other
 * field from one source rather than restating the value at the call site.
 */
export const INPUT_LABEL_MARGIN_BOTTOM = "6px";

/**
 * labelStyles is the one treatment every input label shares so the activity
 * (Select), name (TextInput), and collection (PillsInput) labels read
 * identically: the dimmed color the section headings use, the xs size + medium
 * weight, and a 6px label-to-input gap. It is folded into inputFieldStyles for the
 * real labeled inputs below (alongside the canonical description size) — and applied
 * directly on the shared InputWrapper so a wrapper-labeled custom control (a
 * SegmentedControl sat inside an Input.Wrapper, which renders its label through
 * the same InputLabel) matches too — because each Mantine input renders its own
 * label through its wrapper, so a theme-level InputLabel override never reaches
 * them; this is the same per-component `styles.label` hook the Slider already
 * uses. Forcing the label font size here keeps the labels identical even though
 * the inputs differ in size (the activity select is deliberately larger).
 *
 * Canonical form rhythm (the studio sidenav panels and the form modals share
 * it): every labeled control renders at `size:"sm"` (the ~36px input height the
 * activity select alone overrides, larger by design), so a Select, a NumberInput,
 * a Textarea, and a TextInput sit at one height. Inter-field spacing comes from
 * the field container's `gap` token (the panels wrap their fields in a Stack at
 * the `md` gap) — NOT per-control vertical margins: Select/MultiSelect/
 * NumberInput/PillsInput/Textarea carry no `mt`/`mb` here, so adjacent fields are
 * spaced by the one container gap. TextInput/PasswordInput keep `mt:16/mb:24` for
 * the standalone IAM/account vertical forms; a panel using them passes `mt={0}
 * mb={0}` so they sit on the container gap like the rest. The label-to-input gap
 * is the 6px below, uniform across every control through this shared treatment.
 */
const labelStyles = {
  label: {
    color: "var(--mantine-color-dimmed)",
    fontSize: "var(--mantine-font-size-xs)",
    fontWeight: 500,
    marginBottom: INPUT_LABEL_MARGIN_BOTTOM,
  },
} as const;

/**
 * INPUT_DESCRIPTION_FONT_SIZE is the one size every help text under a control renders
 * at, regardless of the control's own `size`. Mantine sizes an InputDescription by the
 * input size (`font-size-{size} - rem(2px)`), so a dense `xs` input's description would
 * otherwise read 2px smaller than an `sm` one — and the wizard toggles (Switch) sit at
 * the standard help-text size while the wizard's `xs` Select/MultiSelect descriptions
 * shrink below them. Pinning every input description to `font-size-sm - rem(2px)` (the
 * size an `sm` input's description already is) keeps the toggle help text and the
 * Select/TextInput help text identical on the same surface. Mirroring the formula (not
 * a hardcoded value) keeps them aligned if the font scale changes; on the global `sm`
 * forms it equals Mantine's own default, so it is a no-op there and a fix only where the
 * inputs are denser than `sm`.
 */
const INPUT_DESCRIPTION_FONT_SIZE =
  "calc(var(--mantine-font-size-sm) - 0.125rem * var(--mantine-scale))";

/**
 * descriptionStyles pins an input's help text to the canonical description size. Merged
 * into the labeled inputs' `styles` below alongside labelStyles, so a control's
 * description matches the toggle descriptions on the same surface.
 */
const descriptionStyles = {
  description: { fontSize: INPUT_DESCRIPTION_FONT_SIZE },
} as const;

/**
 * inputFieldStyles is the shared label + description treatment every labeled input
 * registers, so a Select, MultiSelect, TextInput, Textarea, NumberInput, or PillsInput
 * carries both the dimmed xs label and the canonical description size in one place.
 */
const inputFieldStyles = {
  ...labelStyles,
  ...descriptionStyles,
} as const;

/**
 * PASSWORD_MANAGER_IGNORE_PROPS is the opt-out attribute set every non-credential
 * text field spreads into its defaultProps so password managers (1Password,
 * LastPass, Dashlane) stop offering to fill or save on it — a manager popover over a
 * name, description, or search box is noise, not help. Mantine forwards `data-*`
 * straight to the underlying `<input>` (the same path the sibling `autoComplete`
 * default already rides), so these reach the DOM node the managers inspect. It is
 * deliberately NOT registered on PasswordInput (managers must still save and fill
 * credentials) and is cleared on the Ory auth fields (see OryNodeRenderer) so
 * sign-in, registration, and recovery keep their suggestions.
 */
export const PASSWORD_MANAGER_IGNORE_PROPS = {
  "data-1p-ignore": "true",
  "data-form-type": "other",
  "data-lpignore": "true",
} as const;

/**
 * MODAL_CHROME is the one dialog surface every modal wears: a centered card at the md
 * radius, the md padding its header and body share, and the soft xl shadow that lifts it
 * off the scrim. It is registered TWICE below — on `Modal` for the shorthand and on
 * `ModalRoot` for the composed API the AppModal shell uses — because ModalRoot reads its
 * own `useProps("ModalRoot", …)` key and never sees the shorthand's defaults. One const,
 * so the two spellings of the same dialog cannot drift apart.
 */
const MODAL_CHROME = {
  centered: true,
  padding: "md",
  radius: "md",
  shadow: "xl",
} as const;

/**
 * MODAL_OVERLAY is the subtly blurred, 55%-dimmed scrim both modal spellings share; it
 * reaches the shorthand as `Modal`'s `overlayProps` and the composed API as
 * `ModalOverlay`'s own defaults.
 */
const MODAL_OVERLAY = {
  backgroundOpacity: 0.55,
  blur: 2,
} as const;

/**
 * OrganizationBranding is the minimal slice the theme reads — kept local so the
 * theme does not couple to the full generated Organization type (the real type
 * is threaded in when the org domain lands).
 */
export interface OrganizationBranding {
  uiColors: [string, string, string, string, string, string, string, string, string, string];
}

/**
 * buildTheme creates the Mantine theme for the given organization branding (or
 * the default palette). It carries the load-bearing custom tokens (xxs
 * spacing/font/radius scale, the shared input-label treatment) and the
 * component-level defaults that keep every panel and control visually consistent
 * without per-instance prop overrides — controls inherit size, gap, radius, and
 * shadow from here, so a Mantine component in a panel reads the same as one in
 * the studio chrome.
 *
 * Defaults discipline: every component the studio uses registers its size,
 * radius, gap, padding, and variant once here so call sites compose primitives
 * without re-stating the same prop. A per-call deviation stays on the call site
 * (a specific Modal needs a different padding); the defaults catch the common
 * shape across the surface.
 */
export const buildTheme = (branding: OrganizationBranding | null): MantineThemeOverride =>
  createTheme({
    autoContrast: true,
    colors: {
      blue: branding ? branding.uiColors : DEFAULT_BLUE,
    },
    components: {
      ActionIcon: ActionIcon.extend({
        defaultProps: { radius: "sm", size: "md", variant: "default" },
        // App-wide hover affordance for subtle icon triggers (the three-dots
        // action menus, the row grip, and the like): an explicit subtle
        // ActionIcon that names no color hovers to a neutral gray-2 fill with a
        // dark-9 glyph, so a borderless icon button reads as interactive without
        // each call site restating the colors. Mantine's ActionIcon.css consumes
        // these two custom properties, and theme-level vars merge over the
        // variant resolver's own inline vars, so this lands without a CSS module.
        // Scoped to colorless subtle buttons so a colored subtle button keeps its
        // tinted hover.
        vars: (_theme, props) =>
          props.variant === "subtle" && !props.color
            ? {
                root: {
                  "--ai-hover": "var(--mantine-color-gray-2)",
                  "--ai-hover-color": "var(--mantine-color-dark-9)",
                },
              }
            : { root: {} },
      }),
      // Every notice — a form error, a billing banner, a studio nudge — inherits one
      // calm framed look here so an info/warning/error reads identically without any
      // call site restating it: the tinted `light` variant (a soft `*-light` surface
      // with an accent-colored title), a hairline border in the same hue + a rounded
      // icon chip + a semibold accent title + a readable detail body from the classNames
      // module (which resolves only under this real theme), and the md radius matching
      // the Card/Modal rhythm. This is the look the
      // IAM header banners carried as a bespoke Box frame, promoted to the global
      // default. A call site keeps only its semantic `color`; the two filled map/search
      // notices keep `variant="filled"` (the module's accent treatment steps aside for
      // filled so the solid surface is untouched).
      Alert: Alert.extend({
        classNames: {
          icon: alertClasses.icon,
          message: alertClasses.message,
          root: alertClasses.root,
          title: alertClasses.title,
        },
        defaultProps: { radius: "md", variant: "light" },
      }),
      Badge: Badge.extend({
        defaultProps: { radius: "sm", size: "xs", variant: "light" },
      }),
      Button: Button.extend({
        defaultProps: { radius: "sm", size: "xs" },
      }),
      Card: Card.extend({
        defaultProps: { padding: "lg", radius: "md", withBorder: true },
      }),
      // The tick boxes ride the same compact tier as the Switch, SegmentedControl, and
      // Slider below, so a checkbox list reads at the height of the toggles it sits
      // beside rather than Mantine's stock `sm` — which is a full step taller and made
      // the duplicate/publishable-key checkbox rows tower over their `xs` neighbours.
      // Registered here (not per call site) so a checkbox ADDED to any surface later
      // inherits the density instead of shipping at the stock size.
      Checkbox: Checkbox.extend({
        defaultProps: { size: "xs" },
      }),
      // The collection picker builds on a raw Combobox (it needs a creatable row +
      // capped pills the high-level MultiSelect can't express), so give its options
      // the same selected-row treatment the Select/MultiSelect get below: a selected
      // option carries its own right check, so the redundant blue fill is cleared.
      Combobox: Combobox.extend({
        classNames: { option: comboboxClasses.option },
      }),
      Divider: Divider.extend({
        defaultProps: { color: "default-border" },
      }),
      Drawer: Drawer.extend({
        defaultProps: { overlayProps: { blur: 2 }, padding: "md", radius: "md" },
      }),
      Flex: Flex.extend({
        defaultProps: { gap: "xs" },
      }),
      Group: Group.extend({
        defaultProps: { gap: "xs", wrap: "nowrap" },
      }),
      // Mantine's stock indicator dot rides z-index 200, which outranks every sticky
      // sidenav chrome (z-index 3) and leaves a dot inside a scrolling panel painted
      // over the header its row slides under (the co-pilot ask bar's status dot is
      // the visible case). A dot only has to beat the element it is pinned to, so 1
      // is the right floor: it still covers its own child, and it drops back under
      // the chrome that occludes the scrolling panel.
      Indicator: Indicator.extend({
        defaultProps: { zIndex: 1 },
      }),
      // Input.Wrapper renders the label for any custom control wrapped in it (a
      // SegmentedControl that carries no label of its own — the privacy, zone
      // mode/level, import source/granularity, and suggest route-type controls),
      // so it gets the same shared label treatment the real inputs do and a
      // wrapper-labeled control reads identically to a TextInput/Select label.
      InputWrapper: Input.Wrapper.extend({
        styles: labelStyles,
      }),
      // Every modal — a raw Modal, the AppModal shell, and the two fullscreen
      // dialogs — inherits one polished base here so the chrome reads identically
      // without per-instance overrides: a centered surface over a subtly blurred,
      // 55%-dimmed overlay; the soft xl shadow that lifts it off that scrim; and the
      // shared header band + calm md title weight (the classNames module, which
      // resolves only under this real theme). respectReducedMotion (set below)
      // already collapses the default fade-down transition to instant for users who
      // ask for it, so no per-instance transitionProps are needed.
      //
      // The classNames belong on THIS key alone. Mantine resolves a modal's styles
      // through `useStyles({ name: __staticSelector })`, and every part of the
      // composed API passes the literal "Modal" as that selector — so classNames
      // registered on ModalRoot would be silently ignored, while these reach both
      // spellings.
      Modal: Modal.extend({
        classNames: { header: modalClasses.header, title: modalClasses.title },
        defaultProps: { ...MODAL_CHROME, overlayProps: MODAL_OVERLAY },
      }),
      // The close button is sized on its OWN key rather than through Modal's
      // `closeButtonProps`: Mantine shallow-merges defaultProps, so a call site
      // passing `closeButtonProps` (an aria-label, say) used to replace that object
      // wholesale and drop the size with it. Registered here, the size and a
      // per-call label merge independently — and the composed API's
      // <Modal.CloseButton> inherits it with no plumbing at all.
      ModalCloseButton: ModalCloseButton.extend({ defaultProps: { size: "md" } }),
      ModalContent: ModalContent.extend({ defaultProps: { radius: MODAL_CHROME.radius } }),
      ModalOverlay: ModalOverlay.extend({ defaultProps: MODAL_OVERLAY }),
      ModalRoot: ModalRoot.extend({ defaultProps: MODAL_CHROME }),
      MultiSelect: MultiSelect.extend({
        classNames: { option: comboboxClasses.option },
        defaultProps: {
          checkIconPosition: "right",
          comboboxProps: { withinPortal: true },
          size: "sm",
        },
        styles: inputFieldStyles,
      }),
      NavLink: NavLink.extend({
        classNames: { root: navLinkClasses.root },
      }),
      NumberInput: NumberInput.extend({
        defaultProps: { size: "sm" },
        styles: inputFieldStyles,
      }),
      Paper: Paper.extend({
        defaultProps: { radius: "md", shadow: "sm" },
      }),
      PasswordInput: PasswordInput.extend({
        // Mirror the TextInput treatment so password fields on the account
        // security page read identically to the rest of the input surface: the
        // sm size, the shared dimmed label, and the 16/24 vertical rhythm. The
        // visibility toggle and autoComplete stay per-call (a new-password field
        // sets its own autoComplete).
        defaultProps: { mb: 24, mt: 16, size: "sm" },
        styles: inputFieldStyles,
      }),
      PillsInput: PillsInput.extend({
        defaultProps: { size: "sm" },
        styles: inputFieldStyles,
      }),
      Popover: Popover.extend({
        defaultProps: { radius: "md", shadow: "md", withArrow: true },
      }),
      // The radio dot matches the Checkbox above so the two choice controls read as one
      // family at the compact tier. This registers the plain `Radio` only: the picker
      // grids compose `Radio.Card`, which Mantine registers under its own `RadioCard`
      // key and sizes from its card styles, so their bespoke look is untouched.
      Radio: Radio.extend({
        defaultProps: { size: "xs" },
      }),
      ScrollArea: ScrollArea.extend({
        defaultProps: { type: "hover" },
      }),
      SegmentedControl: SegmentedControl.extend({
        defaultProps: { fullWidth: true, radius: "md", size: "xs" },
      }),
      Select: Select.extend({
        classNames: { option: comboboxClasses.option },
        defaultProps: {
          checkIconPosition: "right",
          comboboxProps: { withinPortal: true },
          size: "sm",
        },
        styles: inputFieldStyles,
      }),
      Slider: Slider.extend({
        defaultProps: { size: "xs" },
        // Keep the value label on one line so a timestamp/number tooltip never
        // wraps; slider labels are short, so nowrap is a safe global default.
        styles: { label: { whiteSpace: "nowrap" } },
      }),
      Stack: Stack.extend({
        defaultProps: { gap: "xs" },
      }),
      Switch: Switch.extend({
        defaultProps: { size: "xs" },
        // The toggle itself stays compact at xs, but its description reads at the EXACT
        // size an input/card description renders (the shared INPUT_DESCRIPTION_FONT_SIZE),
        // so a settings toggle's help text matches the help text under a Select/TextInput
        // on the same page rather than the tiny xs-derived Switch default. The labeled
        // inputs below carry the same description size, so the two stay identical.
        styles: descriptionStyles,
      }),
      Text: Text.extend({
        classNames: (_theme, props) => {
          // The one heading treatment every dialog title shares. A centered dialog
          // gets it through Modal.classNames.title above; a full-screen surface's top
          // bar has no Mantine title slot to hang it on, so it reaches the SAME class
          // through this variant rather than restating the size and weight — the two
          // shells cannot drift while they read one declaration.
          if (props.variant === "dialog-title") {
            return { root: modalClasses.title };
          }

          if (props.variant === "section-label") {
            return { root: sectionLabelClasses.sectionLabel };
          }

          return {};
        },
        defaultProps: { size: "sm" },
      }),
      // Textarea was not extended before, so its label read differently from the
      // rest of the form (no dimmed/xs/6px treatment) and its control sat at the
      // default md height. Register the shared sm size + label treatment so a
      // description Textarea reads identically to the Select/TextInput above it.
      // No vertical margins, like the other labeled controls, so it sits on the
      // field container's gap.
      Textarea: Textarea.extend({
        defaultProps: { ...PASSWORD_MANAGER_IGNORE_PROPS, size: "sm" },
        styles: inputFieldStyles,
      }),
      TextInput: TextInput.extend({
        defaultProps: {
          ...PASSWORD_MANAGER_IGNORE_PROPS,
          autoComplete: "off",
          mb: 24,
          mt: 16,
          size: "sm",
        },
        styles: inputFieldStyles,
      }),
      Title: Title.extend({
        defaultProps: { fw: 600 },
      }),
      // Every tooltip reads on one fixed dark surface in both color schemes: Mantine's
      // stock tooltip keeps a dark surface in light mode but flips to a LIGHT surface in
      // dark mode, which made the two-line map-control / reset labels (a white title over
      // a dimmed detail, authored for a dark bubble) render white-on-light and unreadable.
      // Pinning --tooltip-bg to the near-black gray-9 + --tooltip-color to white via the
      // classNames module keeps light mode exactly as it was and stops the dark-mode flip,
      // so every tooltip — single-line and the two-line labels alike — stays legible.
      //
      // `multiline` is on for every tooltip because Mantine's stock bubble is
      // `white-space: nowrap` and only [data-multiline] flips it to `normal`: without it
      // a sentence-long reason runs off the viewport on one line. Paired with the shared
      // cap in the classNames module, a short label still shrinks to fit its text while a
      // long one wraps at ONE width everywhere (see tooltip.module.css for why the cap is
      // a max-width there rather than a `w` prop here).
      //
      // `position` is `right` because the large majority of the tooltips that rely on this
      // default hang off the left-anchored sidenav and the top-left map controls, where a
      // left-pointing bubble aims straight into the panel edge. Direction is owned by the
      // SURFACE, not the feature component: a surface whose controls sit against the
      // opposite edge flips it once for its whole subtree (see buildPanelTheme), so a
      // component passes `position` only for a genuinely vertical tooltip — a table row, a
      // toolbar, a marker label.
      Tooltip: Tooltip.extend({
        classNames: { tooltip: tooltipClasses.tooltip },
        defaultProps: {
          arrowSize: 6,
          fz: "xs",
          multiline: true,
          position: "right",
          radius: "sm",
          withArrow: true,
        },
      }),
    },
    cursorType: "pointer",
    defaultRadius: "sm",
    fontSizes: {
      xxs: "calc(0.625rem * var(--mantine-scale))",
    },
    radius: {
      xxs: "calc(0.25rem * var(--mantine-scale))",
    },
    // Honor the OS "reduce motion" preference across every Mantine transition
    // (panel/dropdown reveals, modals, drawers) so the studio's motion collapses
    // to instant for users who ask for it, rather than each surface guarding
    // reduced-motion on its own.
    respectReducedMotion: true,
    spacing: {
      xxs: "calc(0.25rem * var(--mantine-scale))",
    },
  });

/**
 * buildWizardTheme is the compact theme override the fullscreen wizards
 * (EmbedModal, VideoExportModal) layer over the app's branded theme through a
 * nested MantineThemeProvider. It is a PARTIAL override — it redeclares the labeled
 * input components plus a few chrome controls the dense scope tightens (the Accordion
 * heading, the Tooltip font, and the SegmentedControl size) — so the provider
 * deep-merges it over the global theme per component: the inputs below replace their
 * global entries (denser size + zeroed vertical margins), and every other component,
 * the org palette, the custom tokens, and the inherited color scheme pass through
 * untouched. It carries no branding and constructs no MantineProvider, so the wizard
 * subtree keeps the host's palette and light/dark scheme.
 *
 * The deltas from the global rhythm: every labeled input drops to size `xs` (vs
 * the global `sm`) while keeping the shared labelStyles (the same xs label + 6px
 * gap as the global form rhythm, so a wizard label reads at the app's standard
 * size — the density comes from the inputs, not a shrunken label), and
 * TextInput/PasswordInput zero their `mt`/`mb` so they sit on their container's
 * gap like the studio's own dense panels rather than carrying the standalone-form
 * vertical rhythm. Mantine merges a child component override OVER (not into) the
 * parent's, so each input restates the full config it needs rather than only the
 * deltas. The card pickers (Radio cards, SimpleGrid) are not input components, so
 * they are untouched and keep their global look. The Tooltip drops to the xs font
 * and the SegmentedControl pins the xs size so a wizard hint or segmented toggle
 * matches the dense fields rather than the larger global default — a Tooltip has no
 * `size`, so its font size is the lever, and the global SegmentedControl is already
 * xs, so its pin is a defensive no-op that keeps the dense scope self-contained.
 */
export const buildWizardTheme = (): MantineThemeOverride =>
  createTheme({
    components: {
      // The grouped configuration sections render their titles through Accordion
      // controls; the global control label is larger than the wizard's dense body, so
      // tighten it to an sm/600 heading that sits just above the xs field labels rather
      // than towering over them. One of the few non-input entries this override tightens
      // for the dense scope, alongside the Tooltip font and the SegmentedControl size.
      Accordion: Accordion.extend({
        styles: {
          label: { fontSize: "var(--mantine-font-size-sm)", fontWeight: 600 },
        },
      }),
      // Input.Wrapper renders the label for any custom control wrapped in it (the
      // unit-picker grids, the tint/base-map card grids), so it carries the shared
      // label too — a wrapper-labeled control reads identically to the real inputs.
      InputWrapper: Input.Wrapper.extend({
        styles: labelStyles,
      }),
      MultiSelect: MultiSelect.extend({
        classNames: { option: comboboxClasses.option },
        defaultProps: {
          checkIconPosition: "right",
          comboboxProps: { withinPortal: true },
          size: "xs",
        },
        styles: inputFieldStyles,
      }),
      NumberInput: NumberInput.extend({
        defaultProps: { size: "xs" },
        styles: inputFieldStyles,
      }),
      PasswordInput: PasswordInput.extend({
        defaultProps: { mb: 0, mt: 0, size: "xs" },
        styles: inputFieldStyles,
      }),
      PillsInput: PillsInput.extend({
        defaultProps: { size: "xs" },
        styles: inputFieldStyles,
      }),
      // The global theme already sizes SegmentedControl at xs; pin it here too so the
      // wizard's dense scope stays explicit and self-contained rather than leaning on the
      // global default leaking through the deep-merge. A defensive no-op today.
      SegmentedControl: SegmentedControl.extend({ defaultProps: { size: "xs" } }),
      Select: Select.extend({
        classNames: { option: comboboxClasses.option },
        defaultProps: {
          checkIconPosition: "right",
          comboboxProps: { withinPortal: true },
          size: "xs",
        },
        styles: inputFieldStyles,
      }),
      Textarea: Textarea.extend({
        defaultProps: { ...PASSWORD_MANAGER_IGNORE_PROPS, size: "xs" },
        styles: inputFieldStyles,
      }),
      TextInput: TextInput.extend({
        defaultProps: {
          ...PASSWORD_MANAGER_IGNORE_PROPS,
          autoComplete: "off",
          mb: 0,
          mt: 0,
          size: "xs",
        },
        styles: inputFieldStyles,
      }),
      // A wizard tooltip otherwise renders at Mantine's default font size, visibly larger
      // than the dense xs fields it sits among; shrink its font to xs so a hint matches
      // the compact scope. Tooltip exposes no `size`, so the font-size style prop is the
      // lever.
      Tooltip: Tooltip.extend({ defaultProps: { fz: "xs" } }),
    },
  });

/**
 * buildPanelTheme is the compact theme override the studio's floating chrome layers over
 * the app's branded theme through a nested MantineThemeProvider: the layers and
 * preferences panels (mounted by FloatingOverlay) and the POI filter dropdown, which sits
 * in a Menu.Dropdown and so mounts the provider itself. Like buildWizardTheme it is a
 * PARTIAL override — it redeclares the labeled inputs those surfaces render plus the
 * Tooltip direction — so the provider deep-merges it over the global theme per component:
 * the inputs below replace their global entries, and the org palette, the custom tokens,
 * the inherited color scheme, and every component it does not name pass through untouched.
 * It carries no branding and constructs no MantineProvider, so the panel subtree keeps the
 * host's palette and light/dark scheme.
 *
 * It is kept SEPARATE from buildWizardTheme and buildIamTheme even though all three land
 * on `xs` today: a fullscreen wizard, a settings form, and a card floating over the map
 * are three different densities to tune, and one shared builder would couple them.
 *
 * The deltas from the global rhythm: every labeled input drops to size `xs` (vs the global
 * `sm`) while keeping the shared labelStyles, and every one of them zeroes its `mt`/`mb`.
 * A floating panel is a narrow card sat over the map whose neighbours — Switch,
 * SegmentedControl, Slider, Checkbox, Radio — are all `xs` globally, so an `sm` Select
 * beside them reads a full step too large; and the standalone-form vertical rhythm the
 * global TextInput/PasswordInput carry (`mt:16`/`mb:24`) pushes fields apart inside a card
 * whose sections already space themselves on a Stack gap. Each input restates the full
 * config it needs rather than only the size delta, so the dense scope is self-contained
 * and reads in one place.
 *
 * The Tooltip pin is the other half. The global default points a tooltip `right` — correct
 * for the left-anchored sidenav and the top-left map controls that rely on it — but these
 * panels are anchored against the map's RIGHT edge, so theirs point `left`: inward over the
 * map instead of off the viewport. The direction is owned by the surface, once, here, which
 * is what lets a control inside a floating panel drop `position` entirely.
 */
export const buildPanelTheme = (): MantineThemeOverride =>
  createTheme({
    components: {
      MultiSelect: MultiSelect.extend({
        classNames: { option: comboboxClasses.option },
        defaultProps: {
          checkIconPosition: "right",
          comboboxProps: { withinPortal: true },
          mb: 0,
          mt: 0,
          size: "xs",
        },
        styles: inputFieldStyles,
      }),
      NumberInput: NumberInput.extend({
        defaultProps: { mb: 0, mt: 0, size: "xs" },
        styles: inputFieldStyles,
      }),
      Select: Select.extend({
        classNames: { option: comboboxClasses.option },
        defaultProps: {
          checkIconPosition: "right",
          comboboxProps: { withinPortal: true },
          mb: 0,
          mt: 0,
          size: "xs",
        },
        styles: inputFieldStyles,
      }),
      Textarea: Textarea.extend({
        defaultProps: { ...PASSWORD_MANAGER_IGNORE_PROPS, mb: 0, mt: 0, size: "xs" },
        styles: inputFieldStyles,
      }),
      TextInput: TextInput.extend({
        defaultProps: {
          ...PASSWORD_MANAGER_IGNORE_PROPS,
          autoComplete: "off",
          mb: 0,
          mt: 0,
          size: "xs",
        },
        styles: inputFieldStyles,
      }),
      // The one non-input entry: a panel anchored on the map's right edge points its
      // tooltips inward, back over the map. Only the direction is restated — the font,
      // arrow, radius, wrap, and dark surface all deep-merge through from the global entry.
      Tooltip: Tooltip.extend({ defaultProps: { position: "left" } }),
    },
  });

/**
 * buildIamTheme is the compact theme override the organization and account settings
 * pages run under (via IamThemeProvider, mounted once in IamPageContainer so every
 * IAM page inherits it). It steps every form control the IAM pages use down to the
 * `xs` tier so a Select, MultiSelect, SegmentedControl, NumberInput, Textarea,
 * TextInput, and the usage page's DatePickerInput read at one dense height — the
 * same compact size the map studio uses,
 * kept as a SEPARATE override so the IAM density and the studio density can be tuned
 * independently without one affecting the other. It is a partial override (only
 * `size`), so it deep-merges over the branded app theme: org branding, the shared
 * label/description styles, the combobox option treatment, and the portal/check-icon
 * defaults all survive — only the control size drops.
 */
export const buildIamTheme = (): MantineThemeOverride =>
  createTheme({
    components: {
      // DatePickerInput ships from @mantine/dates, which this package does not
      // depend on, so it registers by key (no .extend): the usage page's date-range
      // picker must sit at the same xs height as the Selects and the
      // SegmentedControl sharing its filter row.
      DatePickerInput: { defaultProps: { size: "xs" } },
      MultiSelect: MultiSelect.extend({ defaultProps: { size: "xs" } }),
      NumberInput: NumberInput.extend({ defaultProps: { size: "xs" } }),
      PasswordInput: PasswordInput.extend({ defaultProps: { size: "xs" } }),
      PillsInput: PillsInput.extend({ defaultProps: { size: "xs" } }),
      SegmentedControl: SegmentedControl.extend({ defaultProps: { size: "xs" } }),
      Select: Select.extend({ defaultProps: { size: "xs" } }),
      Textarea: Textarea.extend({ defaultProps: { ...PASSWORD_MANAGER_IGNORE_PROPS, size: "xs" } }),
      TextInput: TextInput.extend({
        defaultProps: { ...PASSWORD_MANAGER_IGNORE_PROPS, size: "xs" },
      }),
    },
  });
